// ─────────────────────────────────────────────────────────────────────────────────────────────
// DECISION 75: A TYPED MOVE OR CANCEL IN AIDEY CHAT ACTS STRAIGHT AWAY, ALWAYS WITH UNDO.
//
// The chat's own parser ("move the review to Friday at 2", "cancel the review") called App's
// handleAideyEditTask(editObj) and handleAideyDeleteTask(json) with (id, date, time) and (id):
// the wrong shapes. Move matched no task and changed nothing; cancel found no id and showed
// "Couldn't find a task to delete" under the chat's own "Cancelled". Neither had ever worked.
//
// Both now go through here:
//   cancelTask  the task leaves the list at once and the shared delayed delete
//               (ui/useDelayedDelete, decision 7) holds the DELETE for its Undo window; Undo puts
//               it back and nothing was sent; leaving the page sends it at once.
//   moveTask    the move is saved at once; the toast's Undo saves the previous date and time
//               back exactly as they were (datetime, deadline, date and time, nulls included).
// The chat says what happened only after the server answered, never on the click.
// ─────────────────────────────────────────────────────────────────────────────────────────────
import { useCallback, useRef } from 'react';
import { api as defaultApi } from '../api/client';
import { useDelayedDelete } from '../ui/useDelayedDelete';
import { showToast as defaultToast, showErrorToast } from '../utils/toast';
import { messageFromError } from '../utils/apiError';
import { toLocalDateStr } from '../utils/calendarUtils';

const same = (a, b) => String(a) === String(b);
const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// What a move writes, and what Undo writes back. `before` is the task's four time fields exactly
// as they were; `after` keeps the length of a timed task and its time when only a day is given.
export function movePlan(task, date, time) {
  const before = {
    datetime: task.datetime ?? null,
    deadline: task.deadline ?? null,
    date: task.date ?? null,
    time: task.time ?? null,
  };
  const start = task.datetime ? new Date(task.datetime) : null;
  const due = task.deadline ? new Date(task.deadline) : null;
  const anchor = start && !isNaN(start.getTime()) ? start : (due && !isNaN(due.getTime()) ? due : null);
  const newDate = date || (anchor ? toLocalDateStr(anchor) : (task.date ? String(task.date).slice(0, 10) : null));
  const newTime = time || (anchor ? hhmm(anchor) : (task.time || null));
  if (!newDate) return null;
  if (!newTime) return { before, after: { date: newDate } };
  const at = new Date(`${newDate}T${newTime}:00`);
  if (isNaN(at.getTime())) return null;
  if (anchor === start && start) {
    const length = due && !isNaN(due.getTime()) ? due.getTime() - start.getTime() : 60 * 60 * 1000;
    return { before, after: { datetime: at.toISOString(), deadline: new Date(at.getTime() + length).toISOString(), date: newDate, time: newTime } };
  }
  if (anchor === due && due) return { before, after: { deadline: at.toISOString(), date: newDate, time: newTime } };
  return { before, after: { date: newDate, time: newTime } };
}

async function put(apiFn, id, body) {
  const res = await apiFn(`/api/tasks/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  if (res && res.ok === false) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || 'That could not be saved.');
  }
  return res;
}

export default function useChatTaskActions({ tasks, setTasks, apiFn = defaultApi, toast = defaultToast }) {
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const pendingRef = useRef(new Set());       // ids whose DELETE is waiting out its Undo window
  const restoredRef = useRef(new Map());      // id -> the chat's callback when it comes back

  const del = useDelayedDelete({
    send: (t, { keepalive }) => apiFn(`/api/tasks/${t.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (t) => { pendingRef.current.delete(String(t.id)); restoredRef.current.delete(String(t.id)); },
    onRestore: (t, info) => {
      pendingRef.current.delete(String(t.id));
      setTasks((prev) => (prev.some((x) => same(x.id, t.id)) ? prev : [...prev, t]));
      const cb = restoredRef.current.get(String(t.id));
      restoredRef.current.delete(String(t.id));
      cb?.(info);
    },
    describe: (t) => `Cancelled "${t.title}".`,
    failMessage: 'That could not be cancelled.',
  });

  // Returns false when there is no such task. onRestored({ undone, error }) runs if it comes back.
  const cancelTask = useCallback((id, { onRestored } = {}) => {
    const t = (tasksRef.current || []).find((x) => same(x.id, id));
    if (!t) return false;
    pendingRef.current.add(String(t.id));
    if (onRestored) restoredRef.current.set(String(t.id), onRestored);
    setTasks((prev) => prev.filter((x) => !same(x.id, t.id)));
    del.remove(t);
    return true;
  }, [del, setTasks]);

  // Resolves { ok, task, error }. The Undo toast appears only once the move has been saved.
  const moveTask = useCallback(async (id, date, time, { onUndone } = {}) => {
    const t = (tasksRef.current || []).find((x) => same(x.id, id));
    if (!t) return { ok: false, error: 'not-found' };
    const plan = movePlan(t, date, time);
    if (!plan) return { ok: false, error: 'That date or time could not be read.' };
    setTasks((prev) => prev.map((x) => (same(x.id, t.id) ? { ...x, ...plan.after } : x)));
    try {
      await put(apiFn, t.id, plan.after);
    } catch (err) {
      setTasks((prev) => prev.map((x) => (same(x.id, t.id) ? { ...x, ...plan.before } : x)));
      return { ok: false, error: messageFromError(err, 'That could not be moved.') };
    }
    toast(`Moved "${t.title}".`, {
      type: 'info',
      action: {
        label: 'Undo',
        onClick: async () => {
          setTasks((prev) => prev.map((x) => (same(x.id, t.id) ? { ...x, ...plan.before } : x)));
          try {
            await put(apiFn, t.id, plan.before);
            onUndone?.({ ok: true });
          } catch (err) {
            setTasks((prev) => prev.map((x) => (same(x.id, t.id) ? { ...x, ...plan.after } : x)));
            const msg = messageFromError(err, 'That could not be undone.');
            showErrorToast(msg);
            onUndone?.({ ok: false, error: msg });
          }
        },
      },
    });
    return { ok: true, task: t, after: plan.after };
  }, [apiFn, setTasks, toast]);

  // A refetched list must not bring back a task whose DELETE is still waiting out its Undo.
  const withoutPending = useCallback((list) => (
    pendingRef.current.size ? list.filter((t) => !pendingRef.current.has(String(t.id))) : list
  ), []);

  return { cancelTask, moveTask, withoutPending };
}
